/**
 * request.ts — the calculation request schemas for `lamine`, `creux` and `bois`
 * (US-013, FR-21, FR-28, ARCHITECTURE.md §4.5).
 *
 * One schema per module, each describing exactly the field set the tool's own form produces —
 * **30 fields for `lamine`, 33 for `creux`, 24 for `bois`** — as established field by field, with
 * code evidence, in `UNITS.md` §3. Nothing here is re-derived: `UNITS.md` is the source for the
 * field lists, the types, the units and the UI defaults, and where this file makes a judgement that
 * `UNITS.md` does not settle, the judgement is stated in a comment next to the bound it produces.
 *
 * RULES THIS FILE OBEYS
 *  - No formulas. Nothing here computes an engineering value; the only arithmetic is comparison
 *    against a bound.
 *  - No import of `@ronceray/engine`. The contract ships to the browser; the engine does not
 *    (FR-3). `@ronceray/catalog`'s main entry is imported because it is browser-safe by
 *    construction; `@ronceray/catalog/hash` is NOT imported — it needs `node:crypto`.
 *  - Every field carries a `.describe()` with its unit (FR-28), quoted from `UNITS.md`.
 *  - The three module names are reused from `./saved-file` rather than restated, for the same
 *    reason `./errors` reuses them: the domain is the same one and a shape written twice is the bug
 *    (success metric M9).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * NON-FINITE NUMBERS — the mirror image of the decision in `./response`
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * `./response` models engine *output* with a custom `engineNumber` that deliberately ACCEPTS
 * `Infinity`, `-Infinity` and `NaN`, because the frozen engine genuinely emits them and a schema
 * that rejected them would be calling the source of truth invalid.
 *
 * Requests are the opposite case, and this file takes the opposite decision: every numeric field is
 * a plain `z.number()`, which in Zod 4 REJECTS `NaN` and both infinities. That is not an oversight
 * and it is not an inconsistency — it is the same principle applied at the other end of the pipe:
 *
 *  - `UNITS.md` G7 measures what a `NaN` input does. `parseFloat('')` is `NaN`, and the buckling
 *    lengths and moment coefficients (`L`, `Lcry`, `Lcrz`, `LLT`, `lef`, `C1`, `kc`, `Cmy`, `Cmz`,
 *    `CmLT`) are consumed RAW — no `nz()` guard. A `NaN` there produces `NaN` ratios, which the
 *    engine's `max` loop then SKIPS: on the measured IPE 300 baseline, `C1 = NaN` drops the
 *    lateral-torsional-buckling check out of the result entirely and `max` falls from 0.3182 to
 *    0.2096 with `ok` still `true`. A verification silently disappears. G7's conclusion is explicit:
 *    "The request schema must reject `NaN`/missing values for these fields rather than reproduce the
 *    behaviour." This file is where that happens.
 *  - `./saved-file` deliberately accepts `null` in a saved case (a blank numeric field is
 *    `parseFloat('') === NaN`, and `JSON.stringify(NaN)` is `null`) and says in as many words that
 *    refusing it is "left for the request schema to refuse at calculation time, with a per-field
 *    message, where the user can actually see it". `z.number()` rejects `null` too, so that hand-off
 *    lands here as designed.
 *
 * So: non-finite is a legitimate ANSWER and never a legitimate QUESTION.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * WHICH FIELDS CARRY `.default()` — a reconciliation, stated once
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * US-013's acceptance criteria ask for defaults "matching the current UI defaults". `UNITS.md` G7
 * asks for the opposite on ten of those same fields: reject them when missing. Both are right about
 * something, and the reconciliation is:
 *
 *  1. **Every UI default is published, in full, as data** — {@link UI_DEFAULTS} carries all 30 / 33 /
 *     24 values exactly as `FICHES` holds them. A client seeding a blank form, and anyone asking
 *     "what does the tool start with?", reads them from the contract, defined once.
 *  2. **The schema applies `.default()` only where filling in for the caller cannot silently change
 *     the answer.** A default is safe when the omitted value means "none", "the standard value", or
 *     reproduces a fallback the frozen engine already applies (`modeFleche || 'Calculee'`,
 *     `byName(...) || DB.schemas[0]`, `byName(...) || DB.fabrication[0]`, `nz(inp.n1 || 250)`,
 *     `nz(inp.n2 || 350)`, `nz(inp.nlim) || 300`, `nz(inp.km) || 0.7`). Those defaults change no
 *     result: they restate what already happens.
 *  3. **Everything that states a fact about the member is required** — the catalog key, the lengths,
 *     the section dimensions, the applied actions, the loads, the model deflections, the service
 *     class and the load-duration class. Answering those for the caller is exactly the silent-wrong-
 *     answer failure G7 was written about, and the UI's own values are no help: they are a worked
 *     example (an IPN 220 carrying 5.63 kN/m), not neutral values. `qGy` defaulted to 5.63 kN/m
 *     would mean `POST /calc/lamine {}` cheerfully verifying somebody else's beam.
 *
 * The committed invalid-input cases agree: `lamine-missing-required-profil`,
 * `creux-missing-required-section` and `bois-missing-required-classe` all expect a `422`, which is
 * only possible if those fields are required.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * TWO DELIBERATE TIGHTENINGS RELATIVE TO THE ENGINE — validation decisions, not engine changes
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The engine is frozen bug-for-bug (FR-18) and nothing below asks it to change. These are decisions
 * about what the API will *accept*, taken at the only layer where they can be taken safely:
 *
 *  - **`duree` is validated strictly against `CATALOG.duree`.** `UNITS.md` G6: the engine does
 *    `var idx = DB.duree.indexOf(inp.duree); if (idx < 0) idx = 2;` — any unrecognised string
 *    (`'permanente'` lowercase, `'MOYEN TERME'`, `''`, a missing key) silently becomes "Moyen terme"
 *    with no error. A load intended as permanent then gets `kmod = 0.8` instead of `0.6`, making
 *    every timber design strength **33 % too high** and passing checks that should fail. Refusing
 *    the request is the only place this can be stopped without touching the engine.
 *  - **`modeFleche` and `situation` are validated against their two UI options.** G4: the engine
 *    matches them on a single character, case-sensitively (`charAt(0) !== 'C'` selects manual
 *    deflection; `charAt(0) === 'A'` selects the accidental/fire case, which raises every timber
 *    strength via `kfi` and `kmod = 1`). `'saisie'` and a bare `'A'` therefore work by accident, and
 *    `'abc'` silently does not. The form can only ever emit the two listed strings, so accepting
 *    only those costs nothing and closes the same class of hazard as `duree`.
 */

import { CATALOG, CATALOG_VERSION } from '@ronceray/catalog';
import { z } from 'zod';

import type { CatalogTable } from './errors';
import { type SavedModule, SAVED_FILE_MODULES } from './saved-file';

/* ================================================================================================
 * Catalog-derived name sets — read from CATALOG at module load, never hard-coded
 * ============================================================================================== */

/**
 * Narrow a catalog-derived name list to a non-empty tuple.
 *
 * Two jobs. It gives `z.enum` the non-empty tuple it wants, and it turns "the catalog table this
 * schema validates against is empty" — which would otherwise produce a schema that rejects every
 * value in production, silently — into a loud failure at import time. The generated catalog cannot
 * be empty (`catalog.generated.ts` records 133 / 5 / 5 / 145 / 2 / 16 / 5 rows), so this throw is
 * unreachable with real data; it exists so that a broken regeneration cannot ship as a validation
 * layer that says no to everything.
 */
function nonEmptyNames(values: readonly string[], table: string): [string, ...string[]] {
  const [first, ...rest] = values;
  if (first === undefined) {
    throw new Error(
      `@ronceray/contract: catalog table "${table}" is empty, so no name could ever validate. ` +
        `Check @ronceray/catalog (catalog version ${CATALOG_VERSION}).`,
    );
  }
  return [first, ...rest];
}

/** The 133 rolled-profile designations, e.g. `'IPE 300'`. Source: `CATALOG.profils[].nom`. */
export const PROFIL_NAMES = nonEmptyNames(
  CATALOG.profils.map((profile) => profile.nom),
  'profils',
);

/** The 5 steel grades, e.g. `'S275'`. Source: `CATALOG.nuances[].n`. */
export const NUANCE_NAMES = nonEmptyNames(
  CATALOG.nuances.map((grade) => grade.n),
  'nuances',
);

/**
 * The 145 hollow-section designations. **Includes the literal `'Personnalise'`**, which is a real
 * catalog row and a valid request value, not an unknown name — see {@link PERSONNALISE_SECTION}.
 * Source: `CATALOG.creux[].nom`.
 */
export const SECTION_CREUSE_NAMES = nonEmptyNames(
  CATALOG.creux.map((section) => section.nom),
  'creux',
);

/** The 16 timber strength classes, e.g. `'C24'`, `'GL28h'`. Source: `CATALOG.bois[].classe`. */
export const CLASSE_BOIS_NAMES = nonEmptyNames(
  CATALOG.bois.map((timberClass) => timberClass.classe),
  'bois',
);

/** The 5 static schemes used for the deflection coefficients. Source: `CATALOG.schemas[].n`. */
export const SCHEMA_STATIQUE_NAMES = nonEmptyNames(
  CATALOG.schemas.map((scheme) => scheme.n),
  'schemas',
);

/** The 2 hollow-section manufacturing routes. Source: `CATALOG.fabrication[].n`. */
export const FABRICATION_NAMES = nonEmptyNames(
  CATALOG.fabrication.map((route) => route.n),
  'fabrication',
);

/**
 * The 5 load-duration classes, longest first. Source: `CATALOG.duree`, whose ORDER is load-bearing:
 * the engine indexes `KmodRow.vals` by position in this list.
 */
export const DUREE_NAMES = nonEmptyNames([...CATALOG.duree], 'duree');

/**
 * The sentinel hollow-section name that switches `calcCreux` to user-entered `hp` / `bp` / `tp`
 * geometry (`UNITS.md` G5: the branch is taken *before* any catalog lookup, so `section` geometry is
 * ignored entirely). It is a genuine catalog row and must never be reported as an unknown entry.
 */
export const PERSONNALISE_SECTION = 'Personnalise';

/**
 * Which request field is backed by which catalog table, per module.
 *
 * `./errors` defines the `404 unknown_catalog_entry` body and its `catalog` enum but has no way to
 * know which *request* field feeds which table; the API needs that mapping to turn a rejected name
 * into the `404` that `ERRORS.md` documents (`profil: "IPE 999"` is a well-formed request for
 * something that does not exist — `404` — whereas `L: -3` is a malformed request — `422`). The
 * mapping lives here, next to the schemas that produce the rejection, and exactly once.
 *
 * `schema`, `fabrication` and `duree` are catalog-backed too but are absent on purpose: they are
 * closed enumerations of the calculation model rather than product catalogues, `./errors`'
 * `CATALOG_TABLES` does not list them, and a client cannot "re-read the right dropdown" for them in
 * any useful sense. A bad value there is a `422`.
 */
export const CATALOG_NAME_FIELDS = {
  lamine: { profil: 'profils', nuance: 'nuances' },
  creux: { section: 'sectionsCreuses', nuance: 'nuances' },
  bois: { classe: 'classesBois' },
} as const satisfies Record<SavedModule, Readonly<Record<string, CatalogTable>>>;

/* ================================================================================================
 * Primitives
 * ============================================================================================== */

/**
 * A name that must be a member of a catalog table.
 *
 * The allowed set is derived from `CATALOG` at module load and never written out here: a hard-coded
 * list would drift the moment the catalog is regenerated, and it would drift *silently*, rejecting
 * a section an engineer can see in the dropdown.
 *
 * The message is written by hand rather than left to Zod's default, which would enumerate all 133
 * profile names — or all 145 hollow sections — inside a `422` body that a browser has to render
 * next to an input box.
 */
function catalogNameSchema(names: readonly [string, ...string[]], what: string) {
  return z.enum(names, {
    error: (issue) =>
      `Unknown ${what}: ${JSON.stringify(issue.input)}. Expected one of the ${names.length} ` +
      `entries in the ${CATALOG_VERSION} catalog.`,
  });
}

/**
 * A finite input number.
 *
 * `z.number()` and nothing else: in Zod 4 it already rejects `NaN`, `Infinity`, `-Infinity`, `null`
 * and strings, which is the whole of what this file needs. It is spelled out as a named constant so
 * that the header's non-finite decision has one place to point at, and so that a future reader does
 * not "helpfully" swap in `z.coerce.number()` — which would turn the committed wrong-type cases
 * (`NEd: "36,2"`, `tp: "4 mm"`, `VEd: "2,9"`, all French decimal commas) from a clean `422` into
 * silent garbage, since `Number('36,2')` is `NaN` and `Number('')` is `0`.
 */
const inputNumber = z.number();

/** A strictly positive input number — the base for every length and section dimension. */
const positiveNumber = inputNumber.positive();

/* ================================================================================================
 * Bounds that are judgement calls
 * ============================================================================================== */

/**
 * Lower bound for γM0 / γM1.
 *
 * A partial factor below 1 would *increase* a resistance, which no Eurocode partial factor does.
 * The French National Annex uses γM0 = γM1 = 1.0 and that is also the UI default, so the bound is
 * `>= 1` and emphatically NOT `> 1`: forcing it above 1 would reject every request the tool itself
 * produces. No upper bound — a larger γM is over-conservative, which is odd but harmless.
 */
const GAMMA_M_MIN = 1;

/**
 * Bounds for the equivalent-uniform-moment factors `Cmy`, `Cmz`, `CmLT`.
 *
 * JUDGEMENT CALL. The engine implements EN 1993-1-1 Annex B (Method 2) — `kyy`, `kzz`, `kyz`, `kzy`
 * are the Annex B forms — and Table B.3 bounds these factors at 0.4 <= Cm <= 1.0, 1.0 being the
 * uniform-moment (conservative) case. The lower bound does a second job: `kzy` contains
 * `0.1 * lamz / (CmLT - 0.25)`, so `CmLT = 0.25` is a division by zero that would produce a
 * non-finite interaction factor and drop a check out of `max` exactly as G7 describes. 0.4 puts that
 * singularity outside the accepted range without special-casing a single value.
 */
const CM_MIN = 0.4;
const CM_MAX = 1;

/**
 * Upper bound for the L/n deflection divisors `n1`, `n2`, `nlim`.
 *
 * JUDGEMENT CALL, and a loose one on purpose. The lower bound is not negotiable — these are
 * denominators (`wlim = L / n1`), so zero divides by zero and a negative inverts the criterion — but
 * there is no code value for "too strict". L/1000 is about the strictest criterion in normal
 * practice (crane runways, brittle finishes); 10 000 is an order of magnitude beyond it, at which
 * point the number is a units mistake rather than a serviceability criterion. Anything between the
 * two is accepted, because an over-strict limit is conservative and therefore harmless.
 */
const DEFLECTION_DIVISOR_MAX = 10_000;

/**
 * Bounds for `kc90`, the EN 1995-1-1 §6.1.5 bearing factor.
 *
 * JUDGEMENT CALL. 1.0 is the general value (kc,90 < 1 would reduce the bearing resistance below the
 * characteristic value, which the clause never does) and 4.0 is the largest tabulated value in
 * §6.1.5(4). The UI default of 1.5 sits inside that range; the UI's own hint says 1.00 is the
 * default and 1.25 to 1.50 apply on a continuous support.
 */
const KC90_MIN = 1;
const KC90_MAX = 4;

/* ================================================================================================
 * Shared enumerations
 * ============================================================================================== */

/**
 * `classeService` — the field the whole request layer has to be careful about.
 *
 * MEASURED, in the live browser (`UNITS.md` §2 and its verification log): the control is a
 * `<select>`, so `lire()` — `o[el.dataset.id] = el.type === 'number' ? parseFloat(el.value) :
 * el.value` — takes the `else` branch and the engine receives the **string `'2'`**, never the number
 * `2`. `el.type` is `'select-one'` and `typeof inp.classeService` is `'string'`. The engine survives
 * only because it coerces at the point of use: `byName(DB.kmod, 'cs', +inp.classeService)`.
 * A field typed `z.number()` here would reject every genuine payload the tool produces.
 *
 * IT IS THE ONLY SELECT WITH THIS PROBLEM. Checked against `UNITS.md` §2's list of string-valued
 * inputs: the other eleven selects (`profil`, `nuance`, `section`, `fabrication`, `classe`, `duree`,
 * `situation`, `modeFleche`, `schema`) all carry genuinely textual values, where "arrives as a
 * string" is simply correct. `classeService` is the one select whose options are numbers.
 *
 * HOW IT IS HANDLED, and why not the alternatives: an explicit six-literal union, normalised to the
 * number by a `transform`.
 *  - `z.coerce.number()` was rejected. It coerces far more than the two forms that actually occur —
 *    `true` becomes 1, `null` and `''` become 0, `'2.0'` becomes 2 — and `0` would then have to be
 *    caught by a separate range check. The committed case `bois-out-of-range-classeService` sends
 *    `'4'` and must be refused; so must `4`, `0`, `2.5` and `'two'`, and the union refuses all of
 *    them by construction rather than by a bound bolted on afterwards.
 *  - A bare union without the transform was rejected too: downstream code should not have to ask
 *    which of two representations it got. Consumers see `1 | 2 | 3`, always a number; the wire
 *    accepts either form.
 */
export const ClasseServiceSchema = z
  .union(
    [z.literal(1), z.literal(2), z.literal(3), z.literal('1'), z.literal('2'), z.literal('3')],
    {
      error: (issue) =>
        `Service class must be 1, 2 or 3 (EN 1995-1-1 §2.3.1.3). Received ` +
        `${JSON.stringify(issue.input)}. The number and the string form are both accepted, ` +
        `because the tool's <select> sends the string.`,
    },
  )
  .transform((value): 1 | 2 | 3 =>
    typeof value === 'number' ? value : (Number(value) as 1 | 2 | 3),
  )
  .describe(
    'Service class per EN 1995-1-1 §2.3.1.3: 1 = heated interior, 2 = sheltered and ventilated, ' +
      '3 = exterior. Dimensionless enumeration, normalised to the number 1, 2 or 3. Accepts the ' +
      "string form as well, because the tool's <select> sends '2', not 2 (UNITS.md §2).",
  );

/**
 * `duree` — the load-duration class. Validated strictly; see the header's tightening note and
 * `UNITS.md` G6 for the 33 %-overstrength failure this prevents.
 */
export const DureeSchema = catalogNameSchema(DUREE_NAMES, 'load-duration class').describe(
  'Load-duration class per EN 1995-1-1 Table 3.1, one of the 5 names in CATALOG.duree ' +
    '(Permanente, Long terme, Moyen terme, Court terme, Instantanee). Dimensionless enumeration. ' +
    'Validated strictly: the engine falls back to "Moyen terme" for any unrecognised string with ' +
    'no error, which makes a permanent load up to 33% too strong (UNITS.md G6).',
);

/** `modeFleche` — computed or manually entered deflection. See the header's tightening note. */
export const ModeFlecheSchema = z
  .enum(['Calculee', 'Saisie manuelle'])
  .describe(
    'Deflection mode. Dimensionless enumeration. "Calculee" computes the deflection from the ' +
      'loads and the static scheme; "Saisie manuelle" substitutes wGman / wQman instead. Note ' +
      'UNITS.md G10: the manual override replaces the STRONG-axis deflection only — wGz / wQz stay ' +
      'computed from qGz / qQz.',
  );

/** `situation` — normal or accidental (fire) design situation. */
export const SituationSchema = z
  .enum(['Normale', 'Accidentelle (incendie)'])
  .describe(
    'Design situation. Dimensionless enumeration. "Accidentelle (incendie)" sets gammaM = 1, ' +
      'kfi = 1.25 and kmod = 1, so it RAISES every timber design strength; "Normale" is the ' +
      'conservative case and the default.',
  );

/** `schema` — the static scheme that supplies the deflection coefficients Cq and Cp. */
export const SchemaStatiqueSchema = catalogNameSchema(
  SCHEMA_STATIQUE_NAMES,
  'static scheme',
).describe(
  'Static scheme, one of the 5 names in CATALOG.schemas. Dimensionless enumeration. It selects ' +
    'the deflection coefficients Cq and Cp; it affects serviceability only, never a ULS ratio.',
);

/** `fabrication` — the hollow-section manufacturing route. */
export const FabricationSchema = catalogNameSchema(
  FABRICATION_NAMES,
  'hollow-section manufacturing route',
).describe(
  'Manufacturing route, one of the 2 names in CATALOG.fabrication (EN 10210 hot-finished, ' +
    'EN 10219 cold-formed). Dimensionless enumeration. It sets the corner-radius multiplier rot ' +
    'and the buckling curve.',
);

/* ================================================================================================
 * The UI defaults, published as data (see the header's reconciliation)
 * ============================================================================================== */

/**
 * `lamine` form defaults, exactly as `FICHES.lamine` holds them (`UNITS.md` §3.1).
 *
 * This is the full set of 30. The schema below applies `.default()` to a subset of them — see the
 * header — and takes its values from here, so the two can never disagree.
 */
export const LAMINE_UI_DEFAULTS = {
  repere: 'Poutre',
  profil: 'IPN 220',
  nuance: 'S275',
  gM0: 1,
  gM1: 1,
  L: 4500,
  Lcry: 4500,
  Lcrz: 4500,
  LLT: 4500,
  C1: 1.13,
  kc: 1,
  Cmy: 0.9,
  Cmz: 0.9,
  CmLT: 0.9,
  NEd: 0,
  MyEd: 36.2,
  MzEd: 0,
  VzEd: 32.2,
  modeFleche: 'Calculee',
  schema: SCHEMA_STATIQUE_NAMES[0],
  qGy: 5.63,
  qQy: 4.5,
  PGy: 0,
  PQy: 0,
  qGz: 0,
  qQz: 0,
  wGman: 0,
  wQman: 0,
  n1: 250,
  n2: 350,
} as const;

/** `creux` form defaults, exactly as `FICHES.creux` holds them (`UNITS.md` §3.2). All 33. */
export const CREUX_UI_DEFAULTS = {
  repere: 'Poteau',
  section: 'SHS 100x100x4',
  hp: 100,
  bp: 100,
  tp: 4,
  fabrication: FABRICATION_NAMES[0],
  nuance: 'S235',
  gM0: 1,
  gM1: 1,
  L: 3000,
  Lcry: 3000,
  Lcrz: 3000,
  LLT: 3000,
  C1: 1,
  Cmy: 0.9,
  Cmz: 0.9,
  NEd: 32,
  MyEd: 0,
  MzEd: 0,
  VzEd: 0,
  TEd: 0,
  modeFleche: 'Calculee',
  schema: SCHEMA_STATIQUE_NAMES[0],
  qGy: 0,
  qQy: 0,
  PGy: 0,
  PQy: 0,
  qGz: 0,
  qQz: 0,
  wGman: 0,
  wQman: 0,
  n1: 250,
  n2: 350,
} as const;

/**
 * `bois` form defaults, exactly as `FICHES.bois` holds them (`UNITS.md` §3.3). All 24.
 *
 * `classeService` is the number `2` here because that is what `FICHES` declares; the rendered
 * `<select>` turns it into the string `'2'` on the wire, and {@link ClasseServiceSchema} accepts
 * both.
 */
export const BOIS_UI_DEFAULTS = {
  repere: 'Solive',
  classe: 'C24',
  classeService: 2,
  duree: 'Moyen terme',
  situation: 'Normale',
  b: 100,
  h: 150,
  L: 4500,
  Lcry: 4500,
  Lcrz: 500,
  lef: 500,
  NEd: 0.6,
  MyEd: 3.2,
  MzEd: 0,
  VEd: 2.9,
  Fc90Ed: 2.9,
  lappui: 80,
  kc90: 1.5,
  kcr: 0.67,
  km: 0.7,
  winstG: 4.3,
  winstQ: 3.45,
  psi2: 0,
  nlim: 300,
} as const;

/** Every module's UI defaults, keyed the way `sauver()` keys its `cas` object. */
export const UI_DEFAULTS = {
  lamine: LAMINE_UI_DEFAULTS,
  creux: CREUX_UI_DEFAULTS,
  bois: BOIS_UI_DEFAULTS,
} as const;

/* ================================================================================================
 * Field groups shared by the two steel modules
 * ============================================================================================== */

/**
 * `repere` — the element label.
 *
 * UI metadata, not an engineering input: `UNITS.md` records that the engine never reads it (it feeds
 * the export filename). Optional and unconstrained beyond being a string, because a request that
 * omits its label is still a complete calculation request — several of the committed invalid-input
 * cases omit it, and so may an API client that has no label to give.
 */
const repereField = z
  .string()
  .optional()
  .describe(
    'Free-text element label, e.g. "Poutre P12". No unit — it is not a physical quantity. UI ' +
      'metadata only: the engine never reads it (it names the export file). Optional.',
  );

/** `nuance` — steel grade, shared by `lamine` and `creux`. */
const nuanceField = catalogNameSchema(NUANCE_NAMES, 'steel grade').describe(
  'Steel grade, one of the 5 names in CATALOG.nuances (S235, S275, S355, S420, S460). ' +
    'Dimensionless catalog key. It selects fy and fu in MPa, split at a flange thickness of 40 mm.',
);

/** γM0 / γM1, shared by `lamine` and `creux`. Defaults to the French NA value of 1. */
const partialFactorFields = {
  gM0: inputNumber
    .min(GAMMA_M_MIN)
    .default(1)
    .describe(
      'Partial factor for cross-section resistance, gammaM0. Dimensionless. Divides every ' +
        'section resistance (NplRd = A*fy/gM0/1000). Must be >= 1; the French National Annex and ' +
        'the tool both use 1.0, which is the default.',
    ),
  gM1: inputNumber
    .min(GAMMA_M_MIN)
    .default(1)
    .describe(
      'Partial factor for member instability, gammaM1. Dimensionless. Divides every buckling ' +
        'resistance (NbyRd = chiY*A*fy/gM1/1000). Must be >= 1; French NA value 1.0, the default.',
    ),
} as const;

/**
 * The three steel buckling lengths. All required, all strictly positive — `UNITS.md` G7: they are
 * consumed raw with no `nz()` guard, so a missing or non-finite value makes a check disappear.
 */
const steelBucklingLengthFields = {
  Lcry: positiveNumber.describe(
    'Buckling length about the strong axis y-y, mm. Feeds Ncry = pi^2*E*Iy/Lcry^2/1000. Required ' +
      'and strictly positive: consumed raw, so a missing value silently voids the buckling check ' +
      '(UNITS.md G7).',
  ),
  Lcrz: positiveNumber.describe(
    'Buckling length about the weak axis z-z, mm. Reduce it for lateral restraints. Feeds ' +
      'Ncrz = pi^2*E*Iz/Lcrz^2/1000. Required and strictly positive (UNITS.md G7).',
  ),
  LLT: positiveNumber.describe(
    'Lateral-torsional buckling length, mm — the spacing of the restraints to the compression ' +
      'flange. Feeds Mcr. Required and strictly positive (UNITS.md G7).',
  ),
} as const;

/**
 * The equivalent-uniform-moment factors. Both steel modules use `Cmy` and `Cmz`; only `lamine` adds
 * `CmLT`. Bounds and their justification: {@link CM_MIN} / {@link CM_MAX}.
 */
const momentFactorFields = {
  Cmy: inputNumber
    .min(CM_MIN)
    .max(CM_MAX)
    .describe(
      'Equivalent uniform moment factor about y-y, Cmy. Dimensionless. EN 1993-1-1 Annex B ' +
        'Table B.3 bounds it to 0.4..1.0; 1.0 (uniform moment) is the conservative value. ' +
        'Required — consumed raw (UNITS.md G7).',
    ),
  Cmz: inputNumber
    .min(CM_MIN)
    .max(CM_MAX)
    .describe(
      'Equivalent uniform moment factor about z-z, Cmz. Dimensionless, 0.4..1.0 per ' +
        'EN 1993-1-1 Annex B Table B.3. Required — consumed raw (UNITS.md G7).',
    ),
} as const;

/**
 * The eight serviceability load / deflection-override fields shared verbatim by `lamine` and
 * `creux`.
 *
 * The loads are required (they state what the member carries) and finite but otherwise unbounded in
 * sign: a negative distributed load is uplift, which is a real load case, and the deflection formula
 * `Cq*q*L^4/(E*I)` is linear in it. The two manual overrides default to 0 because 0 means "no
 * override" and they are inert unless `modeFleche` is "Saisie manuelle".
 *
 * Units note (`UNITS.md` §1): the distributed loads are consumed with NO conversion factor, and that
 * is correct rather than a bug — 1 kN/m is exactly 1 N/mm.
 */
const serviceabilityLoadFields = {
  qGy: inputNumber.describe(
    'Permanent distributed load G on the strong axis, kN/m. Serviceability only. Sign follows ' +
      'the deflection convention (positive = downward); uplift is legitimately negative.',
  ),
  qQy: inputNumber.describe('Variable distributed load Q on the strong axis, kN/m.'),
  PGy: inputNumber.describe(
    'Permanent point load G on the strong axis, kN. Its position is fixed by the static scheme ' +
      "(see the scheme's note in CATALOG.schemas).",
  ),
  PQy: inputNumber.describe('Variable point load Q on the strong axis, kN.'),
  qGz: inputNumber.describe('Permanent distributed load G on the weak axis, kN/m.'),
  qQz: inputNumber.describe('Variable distributed load Q on the weak axis, kN/m.'),
  wGman: inputNumber
    .min(0)
    .default(0)
    .describe(
      'Model-supplied deflection under G, mm. Used only when modeFleche is "Saisie manuelle", ' +
        'where it replaces the computed strong-axis value. Non-negative: it is a magnitude ' +
        'compared against a positive limit, so a negative value could only understate the ' +
        'utilisation. Defaults to 0 = no override.',
    ),
  wQman: inputNumber
    .min(0)
    .default(0)
    .describe(
      'Model-supplied deflection under Q, mm. Used only when modeFleche is "Saisie manuelle". ' +
        'Non-negative; defaults to 0 = no override.',
    ),
} as const;

/**
 * The two L/n serviceability criteria shared by the steel modules.
 *
 * `UNITS.md` §5.5 / G3: these are **divisors, not lengths**. `n1 = 250` means L/250 — 18 mm for a
 * 4500 mm span — and not 250 mm. The defaults reproduce the engine's own fallbacks
 * (`nz(inp.n1 || 250)`, `nz(inp.n2 || 350)`), so they change no result; the lower bound is what the
 * engine's `||` fallback masks, since `n1 = 0` divides by zero.
 */
const deflectionCriterionFields = {
  n1: inputNumber
    .positive()
    .max(DEFLECTION_DIVISOR_MAX)
    .default(250)
    .describe(
      'Total-deflection criterion as the divisor n of L/n. DIMENSIONLESS — not a length: 250 ' +
        'means L/250. Gives wlim = L/n1 in mm. Strictly positive (zero divides by zero).',
    ),
  n2: inputNumber
    .positive()
    .max(DEFLECTION_DIVISOR_MAX)
    .default(350)
    .describe(
      'Variable-load deflection criterion as the divisor n of L/n. DIMENSIONLESS — 350 means ' +
        'L/350. Gives wlimQ = L/n2 in mm. Strictly positive.',
    ),
} as const;

/** `modeFleche` + `schema`, shared verbatim by the two steel modules. */
const serviceabilityModeFields = {
  modeFleche: ModeFlecheSchema.default('Calculee'),
  schema: SchemaStatiqueSchema.default(SCHEMA_STATIQUE_NAMES[0]),
} as const;

/**
 * `NEd`, shared by all three modules.
 *
 * SIGN CONVENTION (`UNITS.md` G1), and the schema deliberately does NOT constrain it: **positive =
 * compression, negative = traction**. Both signs are legitimate, so there is nothing to reject — but
 * a flipped sign does not raise an error either, it silently verifies the wrong failure mode, which
 * is why the convention is stated on the field itself.
 */
const NEdField = inputNumber.describe(
  'Axial force NEd, kN. SIGN IS LOAD-BEARING: POSITIVE = COMPRESSION, NEGATIVE = TRACTION. Both ' +
    'signs are valid, so neither is rejected; a flipped sign silently verifies the wrong failure ' +
    'mode (UNITS.md G1). NEd = 0 is treated as compression by the timber module.',
);

/* ================================================================================================
 * lamine — 30 fields (UNITS.md §3.1)
 * ============================================================================================== */

/**
 * Request body for `MOTEUR.calcLamine` — rolled I / H / U profiles, NF EN 1993-1-1 and the French
 * National Annex. Exactly the 30 fields of `FICHES.lamine`.
 *
 * Unknown keys are stripped rather than preserved (the opposite of `./response`, which keeps
 * everything the engine emits): on the way IN, an unrecognised key is a typo or an obsolete field
 * from an old saved file, and forwarding it to the engine would be how a misspelled `Lrcy` looks
 * like it did something.
 */
export const LamineRequestSchema = z
  .object({
    repere: repereField,
    profil: catalogNameSchema(PROFIL_NAMES, 'profile designation').describe(
      'Rolled-profile designation, one of the 133 names in CATALOG.profils, e.g. "IPE 300". ' +
        'Dimensionless catalog key. Required: without it there is no section to verify.',
    ),
    nuance: nuanceField,
    ...partialFactorFields,
    L: positiveNumber.describe(
      'Span of the element, mm. Serviceability only for steel (UNITS.md G2): it reaches the ' +
        'deflection helper and nothing else, so changing L alone changes no ULS ratio.',
    ),
    ...steelBucklingLengthFields,
    C1: positiveNumber.describe(
      'Critical-moment factor C1. Dimensionless; multiplies Mcr directly. 1.00 is uniform moment ' +
        '(conservative), 1.13 a uniformly distributed load. Required and strictly positive — ' +
        'consumed raw, and a missing value makes the lateral-torsional-buckling check vanish ' +
        'from `max` with ok still true (UNITS.md G7, measured).',
    ),
    kc: inputNumber
      .positive()
      .max(1)
      .describe(
        'Moment-diagram correction factor kc, EN 1993-1-1 Table 6.6. Dimensionless, (0, 1]; ' +
          'every tabulated value is <= 1 and 1.00 is the conservative choice. Required — like C1, ' +
          'a missing value silently removes the LTB check (UNITS.md G7).',
      ),
    ...momentFactorFields,
    CmLT: inputNumber
      .min(CM_MIN)
      .max(CM_MAX)
      .describe(
        'Equivalent uniform moment factor for lateral-torsional buckling, CmLT. Dimensionless, ' +
          '0.4..1.0 per EN 1993-1-1 Annex B Table B.3. The lower bound also keeps the request ' +
          'clear of CmLT = 0.25, which is a division by zero inside kzy. Required (UNITS.md G7).',
      ),
    NEd: NEdField,
    MyEd: inputNumber.describe(
      'Bending moment about the strong axis y-y, kN.m. The engine takes its absolute value, so ' +
        'the sign does not affect the result.',
    ),
    MzEd: inputNumber.describe(
      'Bending moment about the weak axis z-z, kN.m. Absolute value is used.',
    ),
    VzEd: inputNumber.describe(
      'Shear force parallel to the web, kN. Absolute value is used. Checked against ' +
        'VplRd = Av*fy/sqrt(3)/gM0/1000.',
    ),
    ...serviceabilityModeFields,
    ...serviceabilityLoadFields,
    ...deflectionCriterionFields,
  })
  .describe(
    'Calculation request for the lamine module (rolled I / H / U profiles, NF EN 1993-1-1 + ' +
      'French NA). 30 fields; units per UNITS.md §3.1 — forces kN, moments kN.m, lengths mm, ' +
      'distributed loads kN/m.',
  );

export type LamineRequest = z.infer<typeof LamineRequestSchema>;
/** The wire shape, before defaults are applied. */
export type LamineRequestInput = z.input<typeof LamineRequestSchema>;

/* ================================================================================================
 * creux — 33 fields (UNITS.md §3.2)
 * ============================================================================================== */

/**
 * Request body for `MOTEUR.calcCreux` — SHS / RHS hollow sections, NF EN 1993-1-1 with EN 10210 /
 * EN 10219. Exactly the 33 fields of `FICHES.creux`.
 *
 * Differences from `lamine`, all from `UNITS.md` §3.2: `profil` becomes `section`; `hp` / `bp` / `tp`
 * / `fabrication` / `TEd` are added; **there is no `kc` and no `CmLT`**.
 */
export const CreuxRequestSchema = z
  .object({
    repere: repereField,
    section: catalogNameSchema(SECTION_CREUSE_NAMES, 'hollow-section designation').describe(
      'Hollow-section designation, one of the 145 names in CATALOG.creux, e.g. "SHS 100x100x4". ' +
        'Dimensionless catalog key. The literal "Personnalise" is a VALID value, not an unknown ' +
        'name: it switches the module to the hp / bp / tp geometry below and ignores the catalog ' +
        'row entirely (UNITS.md G5). Required.',
    ),
    hp: inputNumber
      .optional()
      .describe(
        'Custom section depth h, mm. Read only when section is "Personnalise"; ignored (though ' +
          'still type-checked) for every catalog section. Required and strictly positive when ' +
          '"Personnalise" is selected — see the cross-field rule on this schema.',
      ),
    bp: inputNumber
      .optional()
      .describe(
        'Custom section width b, mm. Read only when section is "Personnalise". Required and ' +
          'strictly positive in that case.',
      ),
    tp: inputNumber
      .optional()
      .describe(
        'Custom wall thickness t, mm. Read only when section is "Personnalise". Required and ' +
          'strictly positive in that case. Not otherwise bounded: the geometric condition ' +
          '2*t < min(h, b) is real but is left unenforced, because rejecting an odd-but-harmless ' +
          'section is worse than letting one through (the engine simply returns a degenerate area).',
      ),
    fabrication: FabricationSchema.default(FABRICATION_NAMES[0]),
    nuance: nuanceField,
    ...partialFactorFields,
    L: positiveNumber.describe(
      'Span or height of the element, mm. Feeds the deflection limit and, uniquely for this ' +
        'module, the torsional rotation theta = |T|*1e6*L/(Gmod*It)*180/pi.',
    ),
    ...steelBucklingLengthFields,
    C1: positiveNumber.describe(
      'Critical-moment factor C1. Dimensionless; multiplies Mcr. Required and strictly positive ' +
        '(UNITS.md G7). Note UNITS.md G8: when h <= b the engine short-circuits Mcr to the ' +
        'sentinel 9999999 kN.m, so C1 has no effect on a square section.',
    ),
    ...momentFactorFields,
    NEd: NEdField,
    MyEd: inputNumber.describe(
      'Bending moment about the strong axis y-y, kN.m. Absolute value is used.',
    ),
    MzEd: inputNumber.describe(
      'Bending moment about the weak axis z-z, kN.m. Absolute value is used.',
    ),
    VzEd: inputNumber.describe(
      'Shear force parallel to h, kN. Absolute value is used; tauVEd = |V|*1000/Av in MPa.',
    ),
    TEd: inputNumber.describe(
      'Saint-Venant torsional moment, kN.m. Absolute value is used; tautEd = |T|*1e6/Wt in MPa. ' +
        'Leave at 0 when there is no torsion.',
    ),
    ...serviceabilityModeFields,
    ...serviceabilityLoadFields,
    ...deflectionCriterionFields,
  })
  .describe(
    'Calculation request for the creux module (SHS / RHS hollow sections, NF EN 1993-1-1 + ' +
      'EN 10210 / EN 10219). 33 fields; units per UNITS.md §3.2.',
  )
  /**
   * CROSS-FIELD RULE — the `Personnalise` path.
   *
   * `UNITS.md` G5: `calcCreux` branches on `inp.section === 'Personnalise'` *before* any catalog
   * lookup and then builds the section from `hp` / `bp` / `tp`. The catalog's own `Personnalise`
   * row is `{h: 0, b: 0, t: 0}` and is unreachable because of that branch order, so if the caller
   * selects the custom path and leaves a dimension out, the engine gets a zero-or-missing
   * dimension and returns a degenerate section — which surfaces much later as a confusing result
   * rather than as "you did not give me a thickness".
   *
   * Expressed as a refinement rather than as conditionally-required fields on purpose: the form
   * sends one flat object with all three keys present whatever `section` says, and a discriminated
   * union or a conditional shape would reject that perfectly ordinary payload. The refinement adds
   * a rule without changing the shape.
   */
  .superRefine((value, ctx) => {
    if (value.section !== PERSONNALISE_SECTION) return;
    for (const field of ['hp', 'bp', 'tp'] as const) {
      const dimension = value[field];
      if (dimension === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message:
            `Required when section is "${PERSONNALISE_SECTION}": the custom geometry is built ` +
            `from hp, bp and tp, and the catalog row of that name is all zeros and unreachable.`,
        });
      } else if (dimension <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message:
            `Must be strictly positive (mm) when section is "${PERSONNALISE_SECTION}". ` +
            `Received ${dimension}.`,
        });
      }
    }
  });

export type CreuxRequest = z.infer<typeof CreuxRequestSchema>;
/** The wire shape, before defaults are applied. */
export type CreuxRequestInput = z.input<typeof CreuxRequestSchema>;

/* ================================================================================================
 * bois — 24 fields (UNITS.md §3.3)
 * ============================================================================================== */

/**
 * Request body for `MOTEUR.calcBois` — timber in combined bending and axial force, NF EN 1995-1-1
 * and the French National Annex. Exactly the 24 fields of `FICHES.bois`.
 *
 * Unlike the steel modules, this one does not compute deflections from loads: `winstG` and `winstQ`
 * are supplied by the caller's model, and `L` reaches only the deflection limit.
 */
export const BoisRequestSchema = z
  .object({
    repere: repereField,
    classe: catalogNameSchema(CLASSE_BOIS_NAMES, 'timber strength class').describe(
      'Timber strength class, one of the 16 names in CATALOG.bois — EN 338 solid timber ' +
        '(C14..C40) or EN 14080 glulam (GL24h..GL32c). Dimensionless catalog key. Required: it ' +
        'carries every characteristic strength, in MPa, plus gammaM, betac, kfi and the size-effect ' +
        'parameters.',
    ),
    classeService: ClasseServiceSchema,
    duree: DureeSchema,
    situation: SituationSchema.default('Normale'),
    b: positiveNumber.describe(
      'Section width, mm. Gives A = b*h in mm2 and Wy = b*h^2/6 in mm3. Strictly positive.',
    ),
    h: positiveNumber.describe(
      'Section depth, mm. Strictly positive. Also compared against the class reference depth ' +
        'khref (mm) for the size-effect factor kh.',
    ),
    L: positiveNumber.describe(
      'Span of the element, mm. Used only for the deflection limit wlim = L/nlim.',
    ),
    Lcry: positiveNumber.describe(
      'Buckling length in the y-y plane, mm. Gives the slenderness lamY = Lcry/iy. Required and ' +
        'strictly positive — consumed raw (UNITS.md G7).',
    ),
    Lcrz: positiveNumber.describe(
      'Buckling length in the z-z plane, mm. Reduce it for lateral restraint. Gives ' +
        'lamZ = Lcrz/iz. Required and strictly positive (UNITS.md G7).',
    ),
    lef: positiveNumber.describe(
      'Effective length for lateral-torsional buckling, mm — the spacing of the restraints to ' +
        'the compression fibre (EN 1995-1-1 Table 6.1). Feeds smcrit = 0.78*b^2*E005/(h*lef) in ' +
        'MPa. Required and strictly positive (UNITS.md G7).',
    ),
    NEd: NEdField,
    MyEd: inputNumber.describe(
      'Bending moment about the strong axis y-y, kN.m. Absolute value is used; ' +
        'smyd = |My|*1e6/Wy in MPa.',
    ),
    MzEd: inputNumber.describe(
      'Bending moment about the weak axis z-z, kN.m. Absolute value is used.',
    ),
    VEd: inputNumber.describe(
      'Shear force, kN. Absolute value is used; taud = 1.5*|V|*1000/(kcr*b*h) in MPa.',
    ),
    Fc90Ed: inputNumber.describe(
      'Support reaction perpendicular to the grain, kN. Absolute value is used; ' +
        'sc90d = |Fc90Ed|*1000/(b*lappui) in MPa.',
    ),
    lappui: positiveNumber.describe(
      'Bearing length at the support, mm. Strictly positive: it divides the bearing stress, and ' +
        'a bearing of zero length carries nothing. (This is the one input that makes the ' +
        'transverse-compression ratio Infinity — the degenerate case ./response is built to ' +
        'tolerate on the way out. Tolerating it as OUTPUT and refusing it as INPUT is the point ' +
        'of having a request schema.)',
    ),
    kc90: inputNumber
      .min(KC90_MIN)
      .max(KC90_MAX)
      .describe(
        'Bearing factor kc,90 per EN 1995-1-1 §6.1.5. Dimensionless, 1.0..4.0; 1.00 is the ' +
          'general value, 1.25 to 1.50 apply on a continuous support. It multiplies a resistance, ' +
          'so it is required rather than defaulted — the UI value of 1.50 is not the neutral one.',
      ),
    kcr: inputNumber
      .positive()
      .max(1)
      .default(0.67)
      .describe(
        'Crack factor kcr. Dimensionless, (0, 1] — it reduces the effective width in ' +
          'taud = 1.5*|V|*1000/(kcr*b*h), so a value above 1 would increase the shear resistance. ' +
          'Defaults to the EN 1995-1-1 / French NA value of 0.67.',
      ),
    km: inputNumber
      .positive()
      .max(1)
      .default(0.7)
      .describe(
        'Biaxial-bending factor km. Dimensionless, (0, 1]. EN 1995-1-1 §6.1.6: 0.7 for ' +
          'rectangular solid sections, 1.0 otherwise. Defaults to 0.7, which is also the ' +
          "engine's own fallback (`nz(inp.km) || 0.7`), so the default changes no result.",
      ),
    winstG: inputNumber
      .min(0)
      .describe(
        "Instantaneous deflection under G, mm, taken from the caller's model. Required: the " +
          'timber module computes no deflection of its own, so this IS the serviceability input. ' +
          'Non-negative — it is a magnitude compared against a positive limit.',
      ),
    winstQ: inputNumber
      .min(0)
      .describe('Instantaneous deflection under Q, mm, from the model. Required, non-negative.'),
    psi2: inputNumber
      .min(0)
      .max(1)
      .describe(
        'Quasi-permanent combination factor psi2 for the variable action, EN 1990 Table A1.1. ' +
          'Dimensionless, 0..1. Used in wfin = wG*(1+kdef) + wQ*(1+psi2*kdef). Required: it is a ' +
          'property of the action category, and the UI value of 0 is the least conservative one.',
      ),
    nlim: inputNumber
      .positive()
      .max(DEFLECTION_DIVISOR_MAX)
      .default(300)
      .describe(
        'Deflection criterion as the divisor n of L/n. DIMENSIONLESS — not a length: 300 means ' +
          'L/300. Gives wlim = L/nlim in mm. Strictly positive (zero divides by zero); the ' +
          "default reproduces the engine's own `nz(inp.nlim) || 300` fallback.",
      ),
  })
  .describe(
    'Calculation request for the bois module (timber in combined bending and axial force, ' +
      'NF EN 1995-1-1 + French NA). 24 fields; units per UNITS.md §3.3.',
  );

export type BoisRequest = z.infer<typeof BoisRequestSchema>;
/** The wire shape, before defaults are applied. */
export type BoisRequestInput = z.input<typeof BoisRequestSchema>;

/* ================================================================================================
 * Generic (module, body) validation
 * ============================================================================================== */

/**
 * The request schema for each module, keyed exactly as `sauver()` keys its `cas` object and as
 * `./saved-file` enumerates the modules.
 *
 * This is the lookup rather than a `z.discriminatedUnion`, because the request bodies carry no
 * discriminator: the tool sends one flat object of form fields, with no `module` key in it. The
 * module is the route (`POST /calc/:module`) and the `cas` key of a saved file, so it always arrives
 * alongside the body rather than inside it.
 */
export const CALC_REQUEST_SCHEMAS = {
  lamine: LamineRequestSchema,
  creux: CreuxRequestSchema,
  bois: BoisRequestSchema,
} as const;

export type CalcRequestSchemaByModule = typeof CALC_REQUEST_SCHEMAS;

/** The parsed request for each module. */
export type CalcRequestByModule = {
  lamine: LamineRequest;
  creux: CreuxRequest;
  bois: BoisRequest;
};

/** The wire shape for each module, before defaults are applied. */
export type CalcRequestInputByModule = {
  lamine: LamineRequestInput;
  creux: CreuxRequestInput;
  bois: BoisRequestInput;
};

/** Any parsed calculation request. */
export type CalcRequest = CalcRequestByModule[SavedModule];

/** Any calculation request as it arrives on the wire. */
export type CalcRequestInput = CalcRequestInputByModule[SavedModule];

/**
 * The request schema for one module, typed precisely when the module is known at the call site.
 *
 * `calcRequestSchemaFor('bois').parse(body)` gives a `BoisRequest`, not a union.
 */
export function calcRequestSchemaFor<M extends SavedModule>(
  module: M,
): CalcRequestSchemaByModule[M] {
  return CALC_REQUEST_SCHEMAS[module];
}

/**
 * Validate `(module, body)` when the module is only known at run time — a saved file fanned out into
 * its three cases, a golden fixture read from disk, a route parameter.
 *
 * Written as a `switch` rather than an indexed lookup plus a cast so that the result type is exact
 * on every branch with no assertion anywhere. On failure the error is a plain Zod error, ready for
 * `zodErrorTo422Body` in `./errors` — which is the only implementation of the `422` body (FR-25),
 * and must stay so.
 */
export function safeParseCalcRequest(
  module: SavedModule,
  body: unknown,
):
  | { readonly success: true; readonly data: CalcRequest }
  | { readonly success: false; readonly error: z.core.$ZodError } {
  switch (module) {
    case 'lamine': {
      const result = LamineRequestSchema.safeParse(body);
      return result.success
        ? { success: true, data: result.data }
        : { success: false, error: result.error };
    }
    case 'creux': {
      const result = CreuxRequestSchema.safeParse(body);
      return result.success
        ? { success: true, data: result.data }
        : { success: false, error: result.error };
    }
    case 'bois': {
      const result = BoisRequestSchema.safeParse(body);
      return result.success
        ? { success: true, data: result.data }
        : { success: false, error: result.error };
    }
  }
}

/**
 * Same as {@link safeParseCalcRequest} but throwing, for call sites that already have an error
 * boundary. The thrown value is a `ZodError`.
 */
export function parseCalcRequest(module: SavedModule, body: unknown): CalcRequest {
  switch (module) {
    case 'lamine':
      return LamineRequestSchema.parse(body);
    case 'creux':
      return CreuxRequestSchema.parse(body);
    case 'bois':
      return BoisRequestSchema.parse(body);
  }
}

/**
 * The three modules, re-exported under the name a request-side caller will look for. It is the same
 * constant as `./saved-file`'s, not a copy: US-017 iterates it to run every golden fixture through
 * "the corresponding request schema", and the set must be the one single set.
 */
export const CALC_MODULES = SAVED_FILE_MODULES;

/* ================================================================================================
 * /optimise — deliberately absent
 * ============================================================================================== */

/**
 * **THERE IS NO `/optimise` CONTRACT IN P0 — NEITHER REQUEST NOR RESPONSE — AND NOTHING ABOVE
 * FREEZES ONE.**
 *
 * The optimiser is UI code in the source file, not an engine function: it reads the DOM and renders
 * HTML. Its port, its golden fixtures and its contract are all deferred to the P1 optimiser PRD (see
 * that PRD's Non-Goals and FR-21), where they will be specified together, because the request shape
 * and the ranking criterion are the same decision. The source audit's record of current behaviour —
 * passing candidates sorted by ascending mass, top 10 shown, section class <= 3 for steel — is the
 * baseline input to that PRD and is deliberately NOT written down as a schema here.
 *
 * Concretely: nothing in this file constrains what `/optimise` may later accept or return. Adding a
 * speculative schema now would be the one way to get that wrong, since a contract is only worth
 * having if it is the thing everybody derives from — and a guessed one would have to be broken.
 */
